<div class="evaluation-runs-container" [class.embedded]="isEmbedded">
  <!-- Header Navigation (hidden when embedded) -->
  @if (!isEmbedded) {
    <app-page-header></app-page-header>
  }

  <div class="content-wrapper" [class.embedded-content]="isEmbedded">
    <!-- Page Header (hidden when embedded) -->
    @if (!isEmbedded) {
    <div class="page-header">
      <button class="back-btn" (click)="goBack()" title="Back to Evaluation">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <div class="header-content">
        <h1 class="page-title">Evaluation Runs - {{ documentModel }}</h1>
        <p class="page-subtitle">View and manage evaluation runs for this document model</p>
        
        <!-- Active Run Status Display (Phase 4) -->
        @if (activeRunStatus) {
          <div class="active-run-status" 
               [class.status-pending]="activeRunStatus.status === 'PENDING'" 
               [class.status-running]="activeRunStatus.status === 'RUNNING'">
            <span class="status-spinner"></span>
            <span class="status-text">
              <strong>{{ activeRunStatus.type }}</strong> {{ activeRunStatus.status }} • 
              Started {{ activeRunStatus.startTime | date:'short' }} • 
              Duration: {{ activeRunStatus.duration }}
            </span>
          </div>
        }
      </div>
      <!-- Run Evaluation Button -->
      <button *ngIf="!authService.isReadOnly" class="btn-primary" 
        (click)="runFullValidation()" 
        [disabled]="isStartingFullEval || hasActiveFullEval"
        [class.btn-loading]="isStartingFullEval"
        [title]="isStartingFullEval || hasActiveFullEval ? 'Evaluation run in progress' : 'Run Evaluation'">
        @if (isStartingFullEval || hasActiveFullEval) {
          <div class="btn-spinner btn-spinner-light"></div>
        } @else {
          <span class="material-icons">play_arrow</span>
        }
        Run Evaluation
      </button>

      <!-- Run Test Button -->
      <button *ngIf="!authService.isReadOnly" class="btn-primary btn-test" 
        (click)="runTestValidation()" 
        [disabled]="isStartingTestEval || hasActiveTestEval"
        [class.btn-loading]="isStartingTestEval"
        [title]="isStartingTestEval || hasActiveTestEval ? 'Test run in progress' : 'Run Test'">
        @if (isStartingTestEval || hasActiveTestEval) {
          <div class="btn-spinner btn-spinner-light"></div>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display: flex; align-items: center; justify-content: center;">
            <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
          </svg>
        }
        Run Test
      </button>
    </div>
    }

    <!-- Active Evaluation Status (show immediately when starting OR when running) -->
    @if ((runType === 'evaluation' && (isStartingFullEval || hasActiveFullEval)) || (runType === 'test' && (isStartingTestEval || hasActiveTestEval))) {
      <div class="unified-eval-card active">
        <div class="eval-header">
          <span class="eval-badge running">
            <span class="spinner-small"></span>
            @if ((runType === 'evaluation' ? isStartingFullEval : isStartingTestEval) && !hasActiveEval) {
              Starting Evaluation
            } @else {
              Active Evaluation
            }
          </span>
          <span class="eval-id">
            @if (hasActiveEval) {
              ID: {{ currentActiveEvaluation?.evaluation_id }}
            } @else {
              ID: Initializing...
            }
          </span>
        </div>
        
        <div class="eval-content">
          <div class="metric-circle-section">
            <div class="accuracy-display">
              <div class="accuracy-circle">
                <svg viewBox="0 0 36 36" class="circular-chart progress">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="4" pathLength="100"/>
                  <circle cx="18" cy="18" r="14" fill="none" stroke="#ffffff" stroke-width="4" pathLength="100"
                    [attr.stroke-dasharray]="evaluationProgressPercent + ' 100'"
                    stroke-dashoffset="0" transform="rotate(-90 18 18)"/>
                </svg>
                <span class="accuracy-value">{{ evaluationProgressPercent }}%</span>
              </div>
              <span class="accuracy-label">Progress</span>
            </div>
          </div>

          <div class="details-inline-section">
            <div class="inline-details-row">
              <span class="inline-detail-item">
                <span class="inline-detail-label">Document Model</span>
                <span class="inline-detail-value document-model-value" [title]="documentModel || '-'">
                  {{ documentModel || '-' }}
                </span>
              </span>
              <span class="inline-detail-item">
                <span class="inline-detail-label">Status</span>
                <span class="inline-detail-value status-badge-inline running">
                  @if ((runType === 'evaluation' ? isStartingFullEval : isStartingTestEval) && !hasActiveEval) {
                    STARTING
                  } @else {
                    {{ currentActiveEvaluation?.status?.status || 'RUNNING' }}
                  }
                </span>
              </span>
              <span class="inline-detail-item">
                <span class="inline-detail-label">Started</span>
                <span class="inline-detail-value">{{ (currentActiveEvaluationStartTime | date:'MMM d, hh:mm a') || '—' }}</span>
              </span>
              <span class="inline-detail-item">
                <span class="inline-detail-label">Duration</span>
                <span class="inline-detail-value">{{ elapsedTime || '—' }}</span>
              </span>
              <span class="inline-detail-item">
                <span class="inline-detail-label">OCR Mode</span>
                <span class="inline-detail-value">{{ currentActiveEvaluation?.status?.ocr_mode || '—' }}</span>
              </span>
              <span class="inline-detail-item">
                <span class="inline-detail-label">LLM Model</span>
                <span class="inline-detail-value">{{ currentActiveEvaluation?.status?.llm_model || '—' }}</span>
              </span>
              @if (runType === 'evaluation' && getAvgElapsedTime()) {
                <span class="inline-detail-item">
                  <span class="inline-detail-label">Avg Time Taken</span>
                  <span class="inline-detail-value">{{ getAvgElapsedTime() }}</span>
                </span>
              }
            </div>
          </div>

          <div class="bar-chart-section">
            <div class="bar-chart-card">
              <span class="bar-chart-title">Field Status</span>
              <svg viewBox="0 0 100 20" class="horizontal-bar-chart" style="width: 12rem;" aria-label="Matched and Unmatched fields">
                <rect x="0" y="5" width="100" height="10" rx="5" fill="rgba(255,255,255,0.22)" />
                @if (activeMatchedFieldsPercent >= 100) {
                  <rect x="0" y="5" width="100" height="10" rx="5" fill="#cfe9f3" />
                } @else if (activeMatchedFieldsPercent <= 0) {
                  <rect x="0" y="5" width="100" height="10" rx="5" fill="#ffccdd" />
                } @else {
                  <path [attr.d]="'M 5 5 L ' + activeMatchedFieldsPercent + ' 5 L ' + activeMatchedFieldsPercent + ' 15 L 5 15 A 5 5 0 0 1 0 10 A 5 5 0 0 1 5 5 Z'" fill="#cfe9f3" />
                  <path [attr.d]="'M ' + activeMatchedFieldsPercent + ' 5 L 95 5 A 5 5 0 0 1 100 10 A 5 5 0 0 1 95 15 L ' + activeMatchedFieldsPercent + ' 15 Z'" fill="#ffccdd" />
                }
              </svg>
              <span class="bar-chart-label" style="font-size: 0.75rem; color: white;">{{ activeMatchedFieldsCount }} matched<br />{{ activeUnmatchedFieldsCount }} unmatched</span>
            </div>
          </div>

          <div class="pie-section">
            <div class="file-status-chart">
              <div class="pie-chart-mini">
                <svg viewBox="0 0 36 36" class="circular-chart">
                  <circle cx="18" cy="18" r="9" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="18" pathLength="100"/>
                  <circle cx="18" cy="18" r="9" fill="none" stroke="#eff6ff" stroke-width="18" pathLength="100"
                    [attr.stroke-dasharray]="getActiveFileStatusDashArray('completed')"
                    stroke-dashoffset="0" transform="rotate(-90 18 18)"/>
                  <circle cx="18" cy="18" r="9" fill="none" stroke="#22c55e" stroke-width="18" pathLength="100"
                    [attr.stroke-dasharray]="getActiveFileStatusDashArray('running')"
                    [attr.stroke-dashoffset]="'-' + getActiveFileStatusOffset('running')" transform="rotate(-90 18 18)"/>
                  <circle cx="18" cy="18" r="9" fill="none" stroke="#ef4444" stroke-width="18" pathLength="100"
                    [attr.stroke-dasharray]="getActiveFileStatusDashArray('failed')"
                    [attr.stroke-dashoffset]="'-' + getActiveFileStatusOffset('failed')" transform="rotate(-90 18 18)"/>
                  <circle cx="18" cy="18" r="9" fill="none" stroke="#9ca3af" stroke-width="18" pathLength="100"
                    [attr.stroke-dasharray]="getActiveFileStatusDashArray('pending')"
                    [attr.stroke-dashoffset]="'-' + getActiveFileStatusOffset('pending')" transform="rotate(-90 18 18)"/>
                </svg>
              </div>
              <div class="chart-legend">
                <span class="legend-item"><span class="dot completed"></span><span class="legend-label">Success</span><span class="legend-count">{{ currentActiveEvaluation?.status?.completed_files || 0 }}</span></span>
                @if ((currentActiveEvaluation?.status?.running_files || 0) > 0) {
                  <span class="legend-item"><span class="dot running"></span><span class="legend-label">In Progress</span><span class="legend-count">{{ currentActiveEvaluation?.status?.running_files }}</span></span>
                }
                @if ((currentActiveEvaluation?.status?.failed_files || 0) > 0) {
                  <span class="legend-item"><span class="dot failed"></span><span class="legend-label">Failed</span><span class="legend-count">{{ currentActiveEvaluation?.status?.failed_files }}</span></span>
                }
                @if ((currentActiveEvaluation?.status?.pending_files || 0) > 0) {
                  <span class="legend-item"><span class="dot pending"></span><span class="legend-label">Pending</span><span class="legend-count">{{ currentActiveEvaluation?.status?.pending_files }}</span></span>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else {
      @if (loadingSummary) {
        <!-- Skeleton loader for Latest Run Summary -->
        <div class="unified-eval-card skeleton-card">
          <div class="eval-header">
            <span class="skeleton-badge"></span>
            <span class="skeleton-id"></span>
          </div>
          
          <div class="eval-content">
            <!-- Left Section: Skeleton Details -->
            <div class="eval-details-section">
              <div class="eval-details-row">
                @for (item of [1,2,3,4]; track item) {
                  <div class="eval-stat skeleton-stat">
                    <span class="skeleton-label"></span>
                    <span class="skeleton-value"></span>
                  </div>
                }
              </div>
            </div>
            
            <!-- Right Section: Skeleton Metrics -->
            <div class="eval-metrics-section">
              <!-- Skeleton Pie Chart -->
              <div class="file-status-chart skeleton-chart">
                <div class="pie-chart-mini skeleton-pie"></div>
                <div class="chart-legend skeleton-legend">
                  @for (item of [1,2,3,4]; track item) {
                    <span class="skeleton-legend-item"></span>
                  }
                </div>
              </div>
              
              <!-- Skeleton Accuracy -->
              <div class="accuracy-display skeleton-accuracy">
                <div class="skeleton-circle"></div>
                <span class="skeleton-accuracy-label"></span>
              </div>
            </div>
          </div>
        </div>
      } @else if (showRunSummary && latestRun && latestRunStatus) {
        <!-- Latest Run Summary Card - Unified Design -->
        <div class="unified-eval-card">
          <div class="eval-header">
            <span class="eval-badge" 
              [class.completed]="latestRun.status === 'COMPLETED' || latestRun.status === 'Completed'"
              [class.failed]="latestRun.status === 'FAILED' || latestRun.status === 'Failed'"
              [class.running]="latestRun.status === 'RUNNING' || latestRun.status === 'In Progress'"
              [class.pending]="latestRun.status === 'PENDING' || latestRun.status === 'Pending'">
              @if (latestRun.status === 'FAILED' || latestRun.status === 'Failed') {
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              } @else if (latestRun.status === 'RUNNING' || latestRun.status === 'In Progress') {
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="10 8 16 12 10 16"></polyline>
                </svg>
              } @else {
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              }
              Latest Run Summary
            </span>
            <span class="eval-id">ID: {{ latestRun.evaluationId || editableEvaluationId || '-' }}</span>
          </div>
          
          <div class="eval-content">
            <div class="metric-circle-section">
              <div class="accuracy-display">
                <div class="accuracy-circle">
                  <svg viewBox="0 0 36 36" class="circular-chart accuracy">
                    <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="4" pathLength="100"/>
                    <circle cx="18" cy="18" r="14" fill="none" stroke="#ffffff" stroke-width="4" pathLength="100"
                      [attr.stroke-dasharray]="getLatestAccuracy() + ' 100'"
                      stroke-dashoffset="0" transform="rotate(-90 18 18)"/>
                  </svg>
                  <span class="accuracy-value">{{ getLatestAccuracy() | number:'1.0-0' }}%</span>
                </div>
                <span class="accuracy-label">Accuracy</span>
              </div>
            </div>

            <div class="details-inline-section">
              <div class="inline-details-row">
                <span class="inline-detail-item">
                  <span class="inline-detail-label">Document Model</span>
                  <span class="inline-detail-value document-model-value" [title]="latestRun.documentCode || documentModel || '-'">
                    {{ latestRun.documentCode || documentModel || '-' }}
                  </span>
                </span>
                <span class="inline-detail-item">
                  <span class="inline-detail-label">Status</span>
                  <span class="inline-detail-value status-badge-inline" [class.completed]="latestRun.status === 'COMPLETED' || latestRun.status === 'Completed'" [class.failed]="latestRun.status === 'FAILED' || latestRun.status === 'Failed'" [class.running]="latestRun.status === 'RUNNING' || latestRun.status === 'In Progress'" [class.pending]="latestRun.status === 'PENDING' || latestRun.status === 'Pending'">
                    {{ latestRun.status || 'Unknown' }}
                  </span>
                </span>
                <span class="inline-detail-item">
                  <span class="inline-detail-label">Started</span>
                  <span class="inline-detail-value">{{ (latestRun.createdDate | date:'MMM d, hh:mm a') || '—' }}</span>
                </span>
                <span class="inline-detail-item">
                  <span class="inline-detail-label">Duration</span>
                  <span class="inline-detail-value">{{ latestRun.duration || '—' }}</span>
                </span>
                <span class="inline-detail-item">
                  <span class="inline-detail-label">OCR Mode</span>
                  <span class="inline-detail-value">{{ latestRun.ocrMode || '—' }}</span>
                </span>
                <span class="inline-detail-item">
                  <span class="inline-detail-label">LLM Model</span>
                  <span class="inline-detail-value">{{ latestRun.llmModel || '—' }}</span>
                </span>
              </div>
            </div>

            <div class="bar-chart-section">
              <div class="bar-chart-card">
                <span class="bar-chart-title">Field Status</span>
                <svg viewBox="0 0 100 20" class="horizontal-bar-chart" style="width: 12rem;" aria-label="Matched and Unmatched fields">
                  <rect x="0" y="5" width="100" height="10" rx="5" fill="rgba(255,255,255,0.22)" />
                  @if (getLatestMatchedFieldsPercent() >= 100) {
                    <rect x="0" y="5" width="100" height="10" rx="5" fill="#cfe9f3" />
                  } @else if (getLatestMatchedFieldsPercent() <= 0) {
                    <rect x="0" y="5" width="100" height="10" rx="5" fill="#ffccdd" />
                  } @else {
                    <path [attr.d]="'M 5 5 L ' + getLatestMatchedFieldsPercent() + ' 5 L ' + getLatestMatchedFieldsPercent() + ' 15 L 5 15 A 5 5 0 0 1 0 10 A 5 5 0 0 1 5 5 Z'" fill="#cfe9f3" />
                    <path [attr.d]="'M ' + getLatestMatchedFieldsPercent() + ' 5 L 95 5 A 5 5 0 0 1 100 10 A 5 5 0 0 1 95 15 L ' + getLatestMatchedFieldsPercent() + ' 15 Z'" fill="#ffccdd" />
                  }
                </svg>
                <span class="bar-chart-label" style="font-size: 0.75rem; color: white;">{{ getLatestMatchedFieldsCount() }} matched<br />{{ getLatestUnmatchedFieldsCount() }} unmatched</span>
              </div>
            </div>

            <div class="pie-section">
              <div class="file-status-chart">
                <div class="pie-chart-mini">
                  <svg viewBox="0 0 36 36" class="circular-chart">
                    <circle cx="18" cy="18" r="9" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="18" pathLength="100"/>
                    <circle cx="18" cy="18" r="9" fill="none" stroke="#eff6ff" stroke-width="18" pathLength="100"
                      [attr.stroke-dasharray]="getFileStatusDashArray('completed')"
                      stroke-dashoffset="0" transform="rotate(-90 18 18)"/>
                    <circle cx="18" cy="18" r="9" fill="none" stroke="#22c55e" stroke-width="18" pathLength="100"
                      [attr.stroke-dasharray]="getFileStatusDashArray('running')"
                      [attr.stroke-dashoffset]="'-' + getFileStatusOffset('running')" transform="rotate(-90 18 18)"/>
                    <circle cx="18" cy="18" r="9" fill="none" stroke="#ef4444" stroke-width="18" pathLength="100"
                      [attr.stroke-dasharray]="getFileStatusDashArray('failed')"
                      [attr.stroke-dashoffset]="'-' + getFileStatusOffset('failed')" transform="rotate(-90 18 18)"/>
                    <circle cx="18" cy="18" r="9" fill="none" stroke="#9ca3af" stroke-width="18" pathLength="100"
                      [attr.stroke-dasharray]="getFileStatusDashArray('pending')"
                      [attr.stroke-dashoffset]="'-' + getFileStatusOffset('pending')" transform="rotate(-90 18 18)"/>
                  </svg>
                </div>
                <div class="chart-legend">
                  <span class="legend-item"><span class="dot completed"></span><span class="legend-label">Success</span><span class="legend-count">{{ latestRunStatus.completed_files || 0 }}</span></span>
                  @if ((latestRunStatus.running_files || 0) > 0) {
                    <span class="legend-item"><span class="dot running"></span><span class="legend-label">In Progress</span><span class="legend-count">{{ latestRunStatus.running_files }}</span></span>
                  }
                  @if ((latestRunStatus.failed_files || 0) > 0) {
                    <span class="legend-item"><span class="dot failed"></span><span class="legend-label">Failed</span><span class="legend-count">{{ latestRunStatus.failed_files }}</span></span>
                  }
                  @if ((latestRunStatus.pending_files || 0) > 0) {
                    <span class="legend-item"><span class="dot pending"></span><span class="legend-label">Pending</span><span class="legend-count">{{ latestRunStatus.pending_files }}</span></span>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    }

    <!-- Past Runs Section -->
    <div class="past-runs-section">
      <!-- Run New Validation Button -->
      <div class="run-validation-btn-container">
  <button *ngIf="!authService.isReadOnly" class="btn-primary btn-run-validation" 
    (click)="runFullValidation()" 
    [disabled]="isStartingFullEval || hasActiveFullEval"
    [class.btn-loading]="isStartingFullEval"
    [title]="isStartingFullEval || hasActiveFullEval ? 'Evaluation run in progress' : 'Run Evaluation'">
    @if (isStartingFullEval || hasActiveFullEval) {
      <div class="btn-spinner btn-spinner-light"></div>
    } @else {
      <span class="material-icons">play_arrow</span>
    }
    Run Evaluation
  </button>
        
  <button *ngIf="!authService.isReadOnly" class="btn-primary btn-run-validation btn-test" 
    (click)="runTestValidation()"
    [disabled]="isStartingTestEval || hasActiveTestEval"
    [class.btn-loading]="isStartingTestEval"
    [title]="isStartingTestEval || hasActiveTestEval ? 'Test run in progress' : 'Run Test'">
    @if (isStartingTestEval || hasActiveTestEval) {
      <div class="btn-spinner btn-spinner-light"></div>
    } @else {
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display: flex; align-items: center; justify-content: center;">
        <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z"/>
      </svg>
    }
    Run Test
  </button>
</div>

      <!-- Run Type Toggle Tabs -->
      <div class="run-type-tabs">
        <button 
          type="button"
          class="run-type-tab"
          [class.active]="runType === 'evaluation'"
          (click)="toggleRunType('evaluation')">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 3v18h18"></path>
            <path d="M18 17V9"></path>
            <path d="M13 17V5"></path>
            <path d="M8 17v-3"></path>
          </svg>
          <span>Past Evaluation Runs</span>
          <span class="tab-count">{{ evaluationRuns.length }}</span>
        </button>
        <button 
          type="button"
          class="run-type-tab"
          [class.active]="runType === 'test'"
          (click)="toggleRunType('test')">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 3h6l3 7-6 11-6-11 3-7z"></path>
            <path d="M9 3l6 7"></path>
            <path d="M15 3l-6 7"></path>
          </svg>
          <span>Past Test Runs</span>
          <span class="tab-count">{{ testRuns.length }}</span>
        </button>
      </div>

      <!-- Loading State -->
      @if (loadingPastRuns) {
        <div class="table-loading-state">
          <div class="docsila-loader">
            <div class="beat-loader">
              <div class="beat"></div>
              <div class="beat"></div>
              <div class="beat"></div>
              <div class="beat"></div>
              <div class="beat"></div>
            </div>
            <p class="loader-text">Loading evaluation runs...</p>
          </div>
        </div>
      } @else {
        <!-- Message for test runs -->
        @if (runType === 'test' && testRuns.length === 0) {
          <div class="no-test-runs-message">
            <p>No test runs found for this document model.</p>
            <p class="help-text">Test runs are created when you run evaluation on selected files (not all files).</p>
          </div>
        }

      <div class="runs-table-wrapper">
        <table class="runs-table">
          <thead>
            <tr>
              <th>Evaluation ID</th>
              <th>Status</th>
              <th>Files</th>
              <th>Attributes</th>
              <th>Duration</th>
              <th>Accuracy</th>
              <th>Date</th>
              <th>Time</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @if (displayedRuns.length === 0) {
              <tr>
                <td colspan="9" class="no-data">No past evaluation runs found</td>
              </tr>
            }
            @for (run of displayedRuns; track $index) {
              <tr>
                <td>
                  <code class="eval-id-code">{{ run.runId }}</code>
                </td>
                <td>
                  <span
                    class="status-badge"
                    [class.completed]="run.status === 'Completed' || run.status === 'COMPLETED'"
                    [class.in-progress]="run.status === 'In Progress' || run.status === 'RUNNING'"
                    [class.failed]="run.status === 'Failed' || run.status === 'FAILED'"
                    [class.pending]="run.status === 'Pending' || run.status === 'PENDING'">
                    <span class="status-label">{{ run.status.toUpperCase() }}</span>
                    <button type="button"
                      class="btn-icon-only btn-status-eye status-inline-eye status-inline-info"
                      title="View Evaluation Details"
                      aria-label="View Evaluation Details"
                      (click)="onEvalInfoClick($event, run)">
                      <span class="material-icons status-eye-glyph" aria-hidden="true">info</span>
                    </button>
                  </span>
                </td>
                <td>{{ run.totalFiles ?? '-' }}</td>
                <td>{{ run.attributeCount ?? '-' }}</td>
                <td>
                  <span class="duration-text" [title]="run.durationMs ? (run.durationMs / 1000 | number:'1.0-0') + ' seconds' : 'Unknown'">
                    {{ run.duration || '-' }}
                  </span>
                </td>
                <td>
                  @if (run.loadingAccuracy) {
                    <span class="accuracy-badge loading">
                      <span class="loading-dots">
                        <span></span><span></span><span></span>
                      </span>
                    </span>
                  } @else {
                    <span 
                      class="accuracy-badge"
                      [class.high]="run.accuracy >= 90"
                      [class.medium]="run.accuracy >= 75 && run.accuracy < 90"
                      [class.low]="run.accuracy < 75">
                      {{ run.accuracy | number:'1.2-2' }}%
                    </span>
                  }
                </td>
                <td>{{ run.createdDate | date:'MMM d, y' }}</td>
                <td>{{ run.createdDate | date:'h:mm a' }}</td>
                <td>
                  <button 
                    class="btn-action btn-results btn-icon-only" 
                    (click)="viewValidationResults(run)"
                    title="View Validation Results">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 3v18h18"></path>
                      <path d="M18 17V9"></path>
                      <path d="M13 17V5"></path>
                      <path d="M8 17v-3"></path>
                    </svg>
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      } <!-- end loadingPastRuns else -->
    </div> <!-- end past-runs-section -->
  </div>
</div>

@if (isEvalDetailsPopupOpen && activeEvalDetailsRun) {
  <div #evalDetailsPopup class="status-details-popup"
    [style.left.px]="evalDetailsPopupPosition.left"
    [style.top.px]="evalDetailsPopupPosition.top"
    [style.max-height]="evalDetailsPopupMaxHeight"
    (click)="$event.stopPropagation()">
    <h4 class="status-details-title">Evaluation Details</h4>
    <div class="status-details-grid">
      <span class="status-details-label">OCR Mode</span>
      <span class="status-details-value">{{ activeEvalDetailsRun.ocrMode || '—' }}</span>
      <span class="status-details-label">LLM Model</span>
      <span class="status-details-value">{{ activeEvalDetailsRun.llmModel || '—' }}</span>
    </div>
  </div>
}

<app-file-selection-modal
  [documentModel]="documentModel"
  [files]="getFilesForDocumentModel()"
  [isOpen]="showFileSelectionModal"
  [testRunMode]="isTestRunMode"
  [isLoadingFiles]="isLoadingModalFiles"
  (onConfirm)="handleFileSelectionConfirm($event)"
  (onCancel)="handleFileSelectionCancel()">
</app-file-selection-modal>


